import { configureStore } from '@reduxjs/toolkit';
import { persistReducer, persistStore } from 'redux-persist';
import { PersistPartial } from 'redux-persist/es/persistReducer';
import storage from 'redux-persist/lib/storage'; // defaults to localStorage for web
import { combineReducers } from 'redux';
import themeReducer from '@/app/lib/store/theme-slice';
import scrollReducer from '@/app/lib/store/scroll-slice';
import drawerReducer from '@/app/lib/store/drawer-slice';
import userDrawerReducer from '@/app/lib/store/user-drawer-slice';
import navigationReducer from '@/app/lib/store/navigation-slice';
import menuReducer from '@/app/lib/store/menu-slice';
import userReducer from '@/app/lib/store/user-slice';
import loadingReducer from '@/app/lib/store/loading-slice';
import problemListReducer from '@/app/lib/store/problem-list-slice';
import totalPagesReducer from '@/app/lib/store/total-pages-slice';
import queryReducer from '@/app/lib/store/query-slice';
import searchParamsReducer from '@/app/lib/store/search-params-slice';
import topicReducer from '@/app/lib/store/topic-slice';
import returnToReducer from '@/app/lib/store/return-to-slice';
import authDialogReducer from '@/app/lib/store/auth-dialog-slice';
import protectedRouteReducer from '@/app/lib/store/protected-route-slice';
import signUpSessionReducer from '@/app/lib/store/sign-up-session-slice';
import userSignUpStatusReducer from '@/app/lib/store/user-sign-up-status-slice';
import pendingStateReducer from '@/app/lib/store/pending-slice';
import paymentStatusReducer from '@/app/lib/store/payment-status-slice';

type PersistedState = ReturnType<typeof rootReducer> & PersistPartial;

// Persist configuration
const persistConfig = {
  key: 'root',
  storage,
  whitelist: [
    'themeReducer',
    'deviceReducer',
    'scrollReducer',
    'userReducer',
    'menuReducer',
    'navigationReducer',
    'drawerReducer',
    'userDrawerReducer',
    'totalPagesReducer',
    'queryReducer',
    'searchParamsReducer',
    'topicReducer',
    'returnToReducer',
    'protectedRouteReducer',
    'signUpSessionReducer',
    'userSignUpStatusReducer',
    'paymentStatusReducer'
  ]
};

// Combine reducers
const rootReducer = combineReducers({
  theme: themeReducer,
  scroll: scrollReducer,
  user: userReducer,
  menu: menuReducer,
  navigation: navigationReducer,
  drawer: drawerReducer,
  userDrawer: userDrawerReducer,
  totalPages: totalPagesReducer,
  query: queryReducer,
  loading: loadingReducer,
  problemList: problemListReducer,
  topic: topicReducer,
  returnTo: returnToReducer,
  searchParams: searchParamsReducer,
  authDialog: authDialogReducer,
  protectedRoute: protectedRouteReducer,
  signUpSession: signUpSessionReducer,
  signingUpUser: userSignUpStatusReducer,
  pendingState: pendingStateReducer,
  payment: paymentStatusReducer
});

// Apply persistence to the root reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Configure store
export const store = configureStore({
  reducer: persistedReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST']
      }
    })
});

// Setup persistor
export const persistor = persistStore(store);

// Types for state and dispatch
export type AppState = PersistedState;
export type AppDispatch = typeof store.dispatch;
